/* Continuous customer-card loop. Reuse original cards; never duplicate reviews. */
if (!customElements.get('printed-trust-carousel')) {
  customElements.define('printed-trust-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.track = this.querySelector('.printed-trust__story-track');
      this.window = this.querySelector('.printed-trust__carousel-window');
      this.cards = [...this.querySelectorAll('.printed-trust__story')];
      this.controls = this.querySelector('.printed-trust__carousel-controls');
      if (!this.track || !this.window || this.cards.length < 2 || !this.controls) return;
      this.events = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motion.matches;
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      this.offset = 0;
      this.classList.add('is-continuous');
      const listen = (target, type, handler, extra = {}) => target.addEventListener(type, handler, {signal:this.events.signal, ...extra});
      this.tick = time => {
        this.frame = null;
        if (!this.canRun()) return;
        if (this.lastTime != null) {
          this.offset += Math.min(time - this.lastTime, 64) * 0.026;
          this.render();
        }
        this.lastTime = time;
        this.frame = requestAnimationFrame(this.tick);
      };
      listen(this.querySelector('[data-carousel-toggle]'), 'click', () => {
        this.userPaused = !this.userPaused;
        this.schedule();
      });
      listen(this, 'pointerenter', event => {
        if (event.pointerType !== 'touch') { this.hovered = true; this.schedule(); }
      });
      listen(this, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      listen(this, 'focusin', () => { this.focused = true; this.schedule(); });
      listen(this, 'focusout', event => { this.focused = this.contains(event.relatedTarget); this.schedule(); });
      listen(this.track, 'pointerdown', event => {
        this.userPaused = true;
        if (event.pointerType !== 'mouse') this.drag = {id:event.pointerId, x:event.clientX, y:event.clientY, moving:false};
        this.schedule();
      }, {passive:true});
      listen(this.track, 'pointermove', event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const dx = event.clientX - this.drag.x;
        const dy = event.clientY - this.drag.y;
        if (!this.drag.moving) {
          if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { this.drag = null; return; }
          if (Math.abs(dx) < 8) return;
          this.drag.moving = true;
          this.track.setPointerCapture(event.pointerId);
        }
        this.offset -= dx;
        this.drag.x = event.clientX;
        this.render();
      }, {passive:true});
      for (const type of ['pointerup','pointercancel']) listen(this.track, type, () => { this.drag = null; });
      listen(this.track, 'keydown', event => {
        if (event.target !== this.track || !['ArrowRight','ArrowLeft'].includes(event.key)) return;
        event.preventDefault();
        this.userPaused = true;
        this.offset += event.key === 'ArrowRight' ? this.stride : -this.stride;
        this.render();
        this.schedule();
      });
      this.querySelectorAll('video').forEach(video => {
        for (const type of ['play','pause','ended']) listen(video, type, () => this.schedule());
      });
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this.motion, 'change', () => { if (this.motion.matches) this.userPaused = true; this.schedule(); });
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.window);
      this.visibility = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
      }, {threshold:0.1});
      this.visibility.observe(this.window);
      this.measure();
    }

    measure() {
      const oldStride = this.stride;
      this.stride = this.cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      if (oldStride && this.stride) this.offset = this.offset / oldStride * this.stride;
      this.loopEnabled = this.stride > 0 && this.stride * this.cards.length >= this.window.clientWidth + this.stride;
      this.controls.hidden = !this.loopEnabled;
      this.render();
      this.schedule();
    }

    render() {
      if (!this.stride || !this.loopEnabled) {
        this.offset = 0;
        this.track.style.transform = '';
        return;
      }
      this.offset %= this.stride * this.cards.length;
      while (this.offset >= this.stride) {
        this.track.append(this.track.firstElementChild);
        this.offset -= this.stride;
      }
      while (this.offset < 0) {
        this.track.prepend(this.track.lastElementChild);
        this.offset += this.stride;
      }
      this.track.style.transform = `translate3d(${-this.offset}px,0,0)`;
    }

    canRun() {
      return this.isConnected && this.loopEnabled && this.visible && !document.hidden && !this.hovered && !this.focused && !this.userPaused && ![...this.querySelectorAll('video')].some(video => !video.paused && !video.ended);
    }

    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.lastTime = null;
      this.querySelector('[data-carousel-label]').textContent = this.userPaused ? 'Start scrolling' : 'Pause scrolling';
      this.querySelector('[data-carousel-symbol]').textContent = this.userPaused ? '▷' : 'Ⅱ';
      if (this.canRun()) this.frame = requestAnimationFrame(this.tick);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.events?.abort();
      this.resize?.disconnect();
      this.visibility?.disconnect();
      this.cards?.forEach(card => this.track.append(card));
      if (this.track) this.track.style.transform = '';
      this.classList.remove('is-continuous');
      this.events = null;
    }
  });
}
